import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, User, LogOut, Heart, Building2 } from "lucide-react";
import { useState } from "react";
import { useAuth, loginWithGoogle } from "@/lib/auth";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/listings", label: "Listings" },
  { to: "/sell", label: "Sell" },
  { to: "/services", label: "Services" },
  { to: "/agents", label: "Agents" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  return (
    <header className="sticky top-0 z-50 bg-background/85 backdrop-blur-xl border-b border-border" data-testid="site-header">
      <div className="bg-red-700 text-white text-xs border-b border-red-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-2 flex flex-wrap items-center justify-between gap-2">
          <span className="opacity-90" data-testid="topbar-offices">Our Offices: Kampala · New York City · Boston · Columbus</span>
          <a href="tel:+256700259675" className="flex items-center gap-2 hover:text-accent transition-colors" data-testid="topbar-phone">
            <Phone className="w-3 h-3" /> UG +256 700 259 675 · +256 701 238 066 · +256 704 412 030 · NA +1 614 571 9333 · +1 617 291 6821
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
          <div className="leading-tight">
            <div className="font-display text-2xl font-bold text-foreground tracking-tight">TIGAWA<span className="text-primary"> PROPERTIES</span></div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-muted-foreground">Powered by Tigawa Group USA</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-full border-border"
            onClick={() => navigate("/listings")}
            data-testid="nav-browse-btn"
          >
            Browse
          </Button>
          <Button
            className="rounded-full bg-primary hover:bg-primary/90"
            onClick={() => navigate("/sell")}
            data-testid="nav-list-btn"
          >
            List Your Property
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-1 flex items-center gap-2 rounded-full border border-border hover:bg-muted transition-colors pl-1 pr-3 py-1" data-testid="nav-user-menu">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs font-bold">
                      {(user.name || user.email || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <span className="text-sm font-medium max-w-[80px] truncate">{user.name?.split(" ")[0] || "You"}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-2 text-xs text-muted-foreground font-mono truncate">{user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User className="w-4 h-4" /> My profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-favorites"><Heart className="w-4 h-4" /> Favorites</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-mylistings"><Building2 className="w-4 h-4" /> My listings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout"><LogOut className="w-4 h-4" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" className="rounded-full text-sm" onClick={() => loginWithGoogle("/profile")} data-testid="nav-login-btn">
              Sign in
            </Button>
          )}
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-sm font-medium ${isActive ? "bg-muted" : "hover:bg-muted/60"}`
                }
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </NavLink>
            ))}
            <Button
              className="mt-2 rounded-full bg-primary hover:bg-primary/90"
              onClick={() => { setOpen(false); navigate("/sell"); }}
              data-testid="mobile-list-btn"
            >
              List Your Property
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
